import { Controller } from "@hotwired/stimulus";
import SelectFloatingPanel from "../lib/select_floating_panel";
import SelectSelection from "../lib/select_selection";

// Generic option picker for SelectComponent - it knows nothing about roles, genres or whatever
// collection comes next, only about the radio/checkbox inputs the component rendered.
//
// Those inputs stay the single source of truth: this controller hides the grid they live in and
// puts a nicer UI in front of it, but it only ever toggles their `checked` state. With JS off the
// grid is simply visible and submits on its own.
export default class extends Controller {
  static targets = ["input", "grid", "panel", "chips", "trigger", "triggerLabel", "search", "box"];

  static values = {
    multiple: Boolean,
    searchable: Boolean,
    // Set from the component, never inferred here - see SelectComponent#close_on_pick?.
    closeOnPick: Boolean,
    // A value that contradicts every other one: picking it clears the rest and vice versa.
    exclusive: String,
    removeLabel: String,
  };

  connect() {
    this.optionButtons = [];
    this.optionInputs = [];
    this.activeIndex = -1;
    this.gridTarget.classList.add("hidden");
    this.selection = new SelectSelection(this.inputTargets, {
      multiple: this.multipleValue,
      exclusiveValue: this.hasExclusiveValue ? this.exclusiveValue : undefined,
    });
    this.floatingPanel = new SelectFloatingPanel({
      root: this.element,
      panel: this.panelTarget,
      frame: this.frameElement,
    });
    this.outsideClickHandler = this.closeIfOutside.bind(this);
    this.viewportChangeHandler = this.closeIfFloating.bind(this);
    document.addEventListener("click", this.outsideClickHandler);
    // Capture: a scroll event doesn't bubble, but it is dispatched down to whatever scrolled,
    // so this hears the table's own scrollbar as well as the window's.
    document.addEventListener("scroll", this.viewportChangeHandler, true);
    window.addEventListener("resize", this.viewportChangeHandler);

    if (this.multipleValue) {
      this.renderChips();
    } else {
      this.syncTriggerLabel();
      this.syncSearchLabel();
    }
  }

  disconnect() {
    document.removeEventListener("click", this.outsideClickHandler);
    document.removeEventListener("scroll", this.viewportChangeHandler, true);
    window.removeEventListener("resize", this.viewportChangeHandler);
  }

  // No stopPropagation: the click must reach the document listener so any other open
  // dropdown closes (closeIfOutside skips this one - the target is inside it).
  toggle() {
    if (this.open) this.close();
    else this.filter();
  }

  // Rebuilds the panel from the inputs, filtered by whatever's in the search box. Already picked
  // options drop out in multiple mode (they're chips by then); in single mode the current one
  // stays, highlighted. A searchable single-select's readout is not treated as typed text, or
  // opening the box would offer exactly the one option you are trying to move away from.
  filter() {
    const typed = this.hasSearchTarget ? this.searchTarget.value.trim() : "";
    const query = typed === this.pickedLabel && !this.multipleValue ? "" : typed.toLowerCase();

    this.optionInputs = this.selection.availableInputs(query);

    this.optionButtons = [];
    this.panelTarget.replaceChildren(
      ...this.optionInputs.map((input) => this.buildOption(input))
    );

    this.setOpen(this.optionInputs.length > 0);
    // Highlight the first match so Enter has an obvious target right after typing.
    this.activeIndex = this.open
      ? Math.max(this.optionInputs.indexOf(this.selection.checkedInput), 0)
      : -1;
    this.highlight();
  }

  buildOption(input) {
    const item = document.createElement("li");
    item.setAttribute("role", "presentation");

    const button = document.createElement("button");
    button.type = "button";
    button.setAttribute("role", "option");
    button.className = "menu__item";
    button.textContent = input.dataset.selectLabel;
    // mousedown (not click) + preventDefault: fires before the search input's blur, so picking an
    // option never races the handler that closes the panel.
    button.addEventListener("mousedown", (event) => {
      event.preventDefault();
      this.pick(input);
    });

    item.appendChild(button);
    this.optionButtons.push(button);
    return item;
  }

  // Full keyboard operability - the grid this controller hides is display:none, so without it
  // there is no non-mouse way to pick anything, and Enter would submit the whole form.
  keydown(event) {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        if (!this.open) return this.filter();
        this.activeIndex = Math.min(this.activeIndex + 1, this.optionButtons.length - 1);
        this.highlight();
        break;
      case "ArrowUp":
        if (!this.open) return;
        event.preventDefault();
        this.activeIndex = Math.max(this.activeIndex - 1, 0);
        this.highlight();
        break;
      case "Enter":
        // Only swallow Enter while the panel is open, so an untouched field doesn't trap
        // the user on this control.
        if (!this.open) return;
        event.preventDefault();
        if (this.optionInputs[this.activeIndex]) this.pick(this.optionInputs[this.activeIndex]);
        break;
      case "Escape":
        if (!this.open) return;
        event.preventDefault();
        this.close();
        break;
      case "Backspace":
        if (this.unpickLast()) event.preventDefault();
        break;
    }
  }

  pick(input) {
    this.selection.pick(input);

    if (this.multipleValue) {
      if (this.hasSearchTarget) this.searchTarget.value = "";
      this.renderChips();
    } else {
      this.syncTriggerLabel();
      this.syncSearchLabel();
    }

    // Whether one pick ends it is the component's call (close_on_pick:). If it stays open,
    // filter() rebuilds the list without what was just taken and closes once nothing is left.
    if (this.closeOnPickValue) this.close();
    else this.filter();

    this.focusControl();
  }

  // A soft keyboard does not reliably report Backspace as a keydown - Gboard sends keyCode 229
  // with predictive text on, and the key comes through as "Unidentified" - but it does report the
  // edit it is about to make. Same rule as the keydown case, arriving by the route a phone uses.
  beforeInput(event) {
    if (event.inputType !== "deleteContentBackward") return;
    if (this.unpickLast()) event.preventDefault();
  }

  // Backspace with nothing left to delete in the box itself takes the last pick off - anything
  // else and backspacing through a typo would eat a chip the moment the word ran out. The last
  // one goes first, the way every token field behaves; the chips' × is for reaching one in the
  // middle. Answers whether it took something off, so the caller knows to swallow the key.
  unpickLast() {
    if (!this.multipleValue || !this.hasSearchTarget) return false;
    if (this.searchTarget.value !== "") return false;

    const last = this.selection.lastCheckedInput;
    if (!last) return false;

    this.unpick(last);
    return true;
  }

  // Taking a pick back off - from its own chip, or with Backspace on an empty box.
  unpick(input) {
    this.selection.unpick(input);
    this.renderChips();
    if (this.open) this.filter();
  }

  // Back to whatever opens the panel, so the keyboard doesn't get stranded on an option button
  // that pick() has just replaced.
  focusControl() {
    if (this.hasSearchTarget || this.hasTriggerTarget) this.controlElement.focus();
  }

  // Bound to the search input's blur. Only reachable when an option's mousedown didn't already
  // preventDefault it - i.e. the user genuinely clicked or tabbed away.
  close() {
    this.panelTarget.replaceChildren();
    this.optionButtons = [];
    this.optionInputs = [];
    this.activeIndex = -1;
    this.setOpen(false);
    // Typing "roc" and then clicking away picks nothing, so the box goes back to reporting what
    // is actually selected rather than keeping a half-typed word.
    this.syncSearchLabel();
  }

  closeIfOutside(event) {
    if (!this.element.contains(event.target)) this.close();
  }

  // A panel pinned to the viewport stops following its control the moment anything scrolls,
  // so it closes rather than drifting away from it. Scrolling the option list itself - which
  // is what highlight() does when you arrow past the bottom of it - is not that.
  closeIfFloating(event) {
    if (this.floatingPanel.shouldCloseAfter(event)) this.close();
  }

  setOpen(open) {
    this.panelTarget.classList.toggle("hidden", !open);
    if (open) this.floatingPanel.position();
    else this.floatingPanel.reset();

    this.controlElement.setAttribute("aria-expanded", open ? "true" : "false");
  }

  get controlElement() {
    return this.hasSearchTarget ? this.searchTarget : this.triggerTarget;
  }

  // What the control *looks* like, which is not always what you type into: in a token box the
  // input is only whatever width the chips left it, and a panel pinned to that would be a
  // sliver hanging off the right-hand end of the field.
  get frameElement() {
    return this.hasBoxTarget ? this.boxTarget : this.controlElement;
  }

  get open() {
    return !this.panelTarget.classList.contains("hidden");
  }

  highlight() {
    this.optionButtons.forEach((button, index) => {
      const active = index === this.activeIndex;
      button.classList.toggle("menu__item--active", active);
      button.setAttribute("aria-selected", active ? "true" : "false");
      if (active) button.scrollIntoView({ block: "nearest" });
    });
  }

  syncTriggerLabel() {
    if (!this.hasTriggerLabelTarget) return;

    const checked = this.selection.checkedInput;
    if (checked) this.triggerLabelTarget.textContent = checked.dataset.selectLabel;
  }

  // A searchable single-select has neither of the other two readouts: no trigger to relabel (the
  // search box replaced it) and no chips (those are the multi-select's). Without this a pick left
  // no trace at all, which reads as a broken filter - and the box showing the current pick is how
  // a combobox behaves anyway.
  syncSearchLabel() {
    if (this.multipleValue || !this.hasSearchTarget) return;

    this.searchTarget.value = this.pickedLabel;
  }

  // Focus on a box that is reporting a pick means "choose something else", so the label is
  // selected and the first keystroke replaces it instead of landing on the end of it.
  focused() {
    if (!this.multipleValue) this.searchTarget.select();

    this.filter();
  }

  get pickedLabel() {
    return this.selection.pickedLabel;
  }

  renderChips() {
    this.chipsTarget.replaceChildren(
      ...this.selection.checkedInputs.map((input) => this.buildChip(input))
    );
  }

  buildChip(input) {
    const chip = document.createElement("span");
    chip.className = "chip";
    chip.textContent = input.dataset.selectLabel;

    const remove = document.createElement("button");
    remove.type = "button";
    remove.className = "chip__remove";
    remove.setAttribute("aria-label", this.removeLabelValue);
    remove.textContent = "x";
    remove.addEventListener("click", () => this.unpick(input));

    chip.appendChild(remove);
    return chip;
  }
}
